import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActionIcon,
  Alert,
  Button,
  Group,
  Modal,
  NumberInput,
  Select,
  Stack,
  Table,
  Text,
  Textarea,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle, IconPlus, IconTrash } from '@tabler/icons-react';
import { useBranches } from '../branches/branchesApi';
import { useItems } from '../items/itemsApi';
import { useCreateTransfer, useStocks } from './inventoryApi';

interface Props {
  opened: boolean;
  onClose: () => void;
}

interface DraftLine {
  key: string;
  itemId: string | null;
  quantity: number | '';
}

// Stock transfer modal — from/to branches must differ, source must
// have sufficient on-hand (server enforces; we surface the available
// figure inline so the operator can see it before submitting).
export function CreateTransferModal({ opened, onClose }: Props) {
  const { t } = useTranslation();
  const { data: branches } = useBranches();
  const { data: items } = useItems();
  const create = useCreateTransfer();

  const [fromBranchId, setFromBranchId] = useState<string | null>(null);
  const [toBranchId, setToBranchId] = useState<string | null>(null);
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<DraftLine[]>([emptyLine()]);
  const [error, setError] = useState<string | null>(null);

  // Source stocks let us show "available: N" next to each item picker.
  const { data: sourceStocks } = useStocks(fromBranchId);

  useEffect(() => {
    if (opened) {
      const first = branches?.[0]?.branchId ?? null;
      const second = branches?.[1]?.branchId ?? null;
      setFromBranchId(first);
      setToBranchId(second);
      setNotes('');
      setLines([emptyLine()]);
      setError(null);
    }
  }, [opened, branches]);

  const branchOptions = useMemo(
    () => branches?.map((b) => ({ value: b.branchId, label: b.nameAr })) ?? [],
    [branches],
  );

  // Item options annotated with available stock at the source branch.
  const itemOptions = useMemo(() => {
    const stockByItem = new Map(sourceStocks?.map((s) => [s.itemId, s.onHand]) ?? []);
    return items?.map((i) => {
      const available = stockByItem.get(i.itemId) ?? 0;
      return {
        value: i.itemId,
        label: `${i.sku} · ${i.nameAr} (${available})`,
      };
    }) ?? [];
  }, [items, sourceStocks]);

  const sameBranch = fromBranchId !== null && fromBranchId === toBranchId;
  const valid =
    !!fromBranchId &&
    !!toBranchId &&
    !sameBranch &&
    lines.length > 0 &&
    lines.every((l) => l.itemId && Number(l.quantity) > 0);

  const submit = async () => {
    if (!valid) return;
    setError(null);
    try {
      await create.mutateAsync({
        fromBranchId: fromBranchId!,
        toBranchId: toBranchId!,
        notes: notes.trim() || null,
        lines: lines.map((l) => ({
          itemId: l.itemId!,
          quantity: Number(l.quantity),
        })),
      });
      notifications.show({
        color: 'green',
        title: t('inventory.transfer.success'),
        message: '',
      });
      onClose();
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } }).response?.data?.detail;
      setError(detail ?? (err instanceof Error ? err.message : t('inventory.transfer.failed')));
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={<Text fw={600}>{t('inventory.transfer.create')}</Text>}
      size="xl"
      centered
    >
      <Stack gap="md">
        <Group grow>
          <Select
            label={t('inventory.transfer.from')}
            data={branchOptions}
            value={fromBranchId}
            onChange={setFromBranchId}
            required
          />
          <Select
            label={t('inventory.transfer.to')}
            data={branchOptions}
            value={toBranchId}
            onChange={setToBranchId}
            required
            error={sameBranch ? t('inventory.transfer.sameBranchError') : undefined}
          />
        </Group>
        <Textarea
          label={t('inventory.transfer.notes')}
          value={notes}
          onChange={(e) => setNotes(e.currentTarget.value)}
          minRows={2}
        />

        <div>
          <Group justify="space-between" mb={6}>
            <Text fw={600} size="sm">{t('inventory.transfer.lines')}</Text>
            <Button
              variant="subtle"
              size="xs"
              leftSection={<IconPlus size={14} stroke={1.6} />}
              onClick={() => setLines((prev) => [...prev, emptyLine()])}
            >
              {t('inventory.transfer.addLine')}
            </Button>
          </Group>
          <Table withTableBorder withColumnBorders striped>
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ minWidth: 280 }}>{t('inventory.transfer.item')}</Table.Th>
                <Table.Th style={{ width: 140 }}>{t('inventory.transfer.qty')}</Table.Th>
                <Table.Th style={{ width: 40 }} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {lines.map((line, idx) => (
                <Table.Tr key={line.key}>
                  <Table.Td>
                    <Select
                      data={itemOptions}
                      value={line.itemId}
                      onChange={(v) =>
                        setLines((prev) =>
                          prev.map((l, i) => (i === idx ? { ...l, itemId: v } : l)),
                        )
                      }
                      searchable
                      placeholder={t('inventory.transfer.pickItem') ?? ''}
                      comboboxProps={{ withinPortal: true }}
                    />
                  </Table.Td>
                  <Table.Td>
                    <NumberInput
                      value={line.quantity}
                      onChange={(v) =>
                        setLines((prev) =>
                          prev.map((l, i) =>
                            i === idx
                              ? { ...l, quantity: typeof v === 'string' ? Number(v) : v }
                              : l,
                          ),
                        )
                      }
                      min={0}
                      decimalScale={3}
                    />
                  </Table.Td>
                  <Table.Td>
                    <ActionIcon
                      variant="subtle"
                      color="red"
                      disabled={lines.length === 1}
                      onClick={() => setLines((prev) => prev.filter((_, i) => i !== idx))}
                    >
                      <IconTrash size={16} stroke={1.6} />
                    </ActionIcon>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </div>

        {error && (
          <Alert color="red" icon={<IconAlertCircle size={16} stroke={1.6} />}>{error}</Alert>
        )}

        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={onClose} disabled={create.isPending}>
            {t('common.cancel')}
          </Button>
          <Button color="wheels" onClick={submit} loading={create.isPending} disabled={!valid}>
            {t('inventory.transfer.submit')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function emptyLine(): DraftLine {
  return {
    key: crypto.randomUUID(),
    itemId: null,
    quantity: '',
  };
}
